import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, login, register, request } from '../api';
import brand from '../brand.generated.json';
import { Field } from '../ui';

function useSubmit(fn: () => Promise<void>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main className="auth">
      <div className="auth-card">
        <div className="brand-mark">{brand.cloudConsoleName}</div>
        <h1>{title}</h1>
        {children}
      </div>
    </main>
  );
}

export function SignIn({ go }: { go: (path: string) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const s = useSubmit(async () => {
    await login(email, password);
    go('/');
  });
  return (
    <Frame title="Sign in">
      <form onSubmit={s.submit} className="stack">
        <Field label="Email"><input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Password"><input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {s.error && <div className="alert error" role="alert">{s.error}</div>}
        <button className="btn" disabled={s.busy}>{s.busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
      <p className="muted"><a href="#/forgot">Forgot password?</a> · <a href="#/register">Create an account</a></p>
    </Frame>
  );
}

export function Register({ go }: { go: (path: string) => void }) {
  const [f, setF] = useState({ name: '', email: '', password: '', organization_name: '' });
  const s = useSubmit(async () => {
    await register(f);
    go('/add-restaurant');
  });
  return (
    <Frame title="Create your account">
      <form onSubmit={s.submit} className="stack">
        <Field label="Your name"><input required autoComplete="name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label="Business or group name"><input required value={f.organization_name} onChange={(e) => setF({ ...f, organization_name: e.target.value })} /></Field>
        <Field label="Email"><input type="email" required autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
        <Field label="Password" hint="At least 10 characters, with letters and numbers."><input type="password" required minLength={10} autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
        {s.error && <div className="alert error" role="alert">{s.error}</div>}
        <button className="btn" disabled={s.busy}>Create account</button>
      </form>
      <p className="muted">Already registered? <a href="#/login">Sign in</a></p>
    </Frame>
  );
}

export function Forgot() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const s = useSubmit(async () => {
    await request('POST', '/v1/auth/forgot-password', { email }, false);
    setSent(true);
  });
  return (
    <Frame title="Reset your password">
      {sent ? (
        <p>If an account exists for {email}, we have emailed a reset link. It is valid for one hour.</p>
      ) : (
        <form onSubmit={s.submit} className="stack">
          <Field label="Email"><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          {s.error && <div className="alert error">{s.error}</div>}
          <button className="btn" disabled={s.busy}>Send reset link</button>
        </form>
      )}
      <p className="muted"><a href="#/login">Back to sign in</a></p>
    </Frame>
  );
}

export function ResetPassword({ token, go }: { token: string; go: (p: string) => void }) {
  const [password, setPassword] = useState('');
  const s = useSubmit(async () => {
    await request('POST', '/v1/auth/reset-password', { token, password }, false);
    go('/login');
  });
  return (
    <Frame title="Choose a new password">
      <form onSubmit={s.submit} className="stack">
        <Field label="New password" hint="At least 10 characters, with letters and numbers."><input type="password" required minLength={10} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
        {s.error && <div className="alert error">{s.error}</div>}
        <button className="btn" disabled={s.busy}>Save password</button>
      </form>
    </Frame>
  );
}

export function VerifyEmail({ token }: { token: string }) {
  const [state, setState] = useState<'pending' | 'ok' | 'error'>('pending');
  useEffect(() => {
    request('POST', '/v1/auth/verify-email', { token }, false).then(() => setState('ok')).catch(() => setState('error'));
  }, [token]);
  return (
    <Frame title="Email verification">
      <p>{state === 'pending' ? 'Verifying…' : state === 'ok' ? 'Your email is verified.' : 'This link is invalid or has expired.'}</p>
      <p><a href="#/">Continue</a></p>
    </Frame>
  );
}

export function AcceptInvite({ token, go }: { token: string; go: (p: string) => void }) {
  const s = useSubmit(async () => {
    await request('POST', '/v1/invites/accept', { token });
    go('/');
  });
  return (
    <Frame title="Join organization">
      <p>Accept the invitation with the account you are signed in with. The invitation must have been sent to this email address.</p>
      <form onSubmit={s.submit}>
        {s.error && <div className="alert error">{s.error}</div>}
        <button className="btn" disabled={s.busy}>Accept invitation</button>
      </form>
    </Frame>
  );
}
